import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import flatten from "lodash/flatten";
import styled from "styled-components";
import ComponentPanel from "../../../components/atoms/ComponentPanel/ComponentPanel";
import { Table } from "../../../components/atoms";
import { getCancerHotspotsData } from "../../../state/selectors/CancerHotspotsComponent.selectors";
import Chart from "../../../components/atoms/charts/Chart";
import { StyledChartsGroup } from "../../../common/styles/ChartsStyles";
import {
  getVarsoUrl,
  getTumourFreq,
  getTranscriptUrl,
  formatNumber,
} from "../../../common/helpers/helpers";
import PublicationLinks from "../../../components/Publications/components/PublicationLinks";
import withErrorGuard from "../../../HOC/withErrorGuard";
import { approxSnow, titaniumWhite } from "../../../common/helpers/colors";

const StyledTableWrapper = styled.div`
  overflow-y: auto;
  margin-left: 0px;
  margin-bottom: 2rem;
`;

const firstRowHeaderOrder = [
  { key: "t_ref_count", label: "Reference allele count" },
  { key: "t_alt_count", label: "Alternative allele count" },
  { key: "t_frequency", label: "Frequency" },
  { key: "t_depth", label: "Depth" },
  { key: "n_ref_count", label: "Reference allele count" },
  { key: "n_alt_count", label: "Alternative allele count" },
  { key: "normal_frequency", label: "Frequency" },
  { key: "n_depth", label: "Depth" },
  { key: "pub_med_references", label: "Pubmed" },
];

const secondRowHeaderOrder = [
  { key: "biotype", label: "Biotype" },
  { key: "feature", label: "Feature" },
  { key: "pick", label: "Pick" },
  { key: "uniparc", label: "Uiparc" },
  { key: "ccds", label: "Ccds" },
  { key: "canonical", label: "Canonical" },
  { key: "ensp", label: "ENSP" },
  { key: "cdsposition", label: "CDS Position" },
  { key: "cdnaposition", label: "cDNA Position" },
];

class CancerHotspots extends React.Component {
  constructor() {
    super();
    this.state = {
      columnWidth: "12rem",
    };
  }

  getCanonical = (canonical) => {
    if (canonical) {
      return "True";
    }
    return "False";
  };

  getCcdsUrl = (ccds) => {
    const url = `https://www.ncbi.nlm.nih.gov/CCDS/CcdsBrowse.cgi?REQUEST=CCDS&DATA=${ccds}`;
    return (
      <a href={url} target="_blank">
        {ccds}
      </a>
    );
  };

  getTdData = (row, key) => {
    /* eslint-disable camelcase */
    const {
      t_ref_count,
      t_alt_count,
      n_ref_count,
      n_alt_count,
      pub_med_references: pmids,
      canonical,
      feature,
      ccds,
    } = row;

    switch (key) {
      case "pub_med_references":
        return <PublicationLinks ids={pmids} />;
      case "canonical":
        return this.getCanonical(canonical);
      case "tumor_frequency":
        return t_ref_count && t_alt_count
          ? getTumourFreq(t_alt_count, t_ref_count)
          : "-";
      case "normal_frequency":
        return n_ref_count && n_alt_count
          ? getTumourFreq(n_alt_count, n_ref_count)
          : "-";
      case "t_frequency":
        return t_ref_count && t_alt_count
          ? getTumourFreq(t_alt_count, t_ref_count)
          : "-";
      case "feature":
        return feature ? getTranscriptUrl(feature) : "-";
      case "ccds":
        return ccds ? this.getCcdsUrl(ccds) : "-";
      default:
        return row[key] !== undefined && row[key]
          ? formatNumber(row[key])
          : "-";
    }
    /* eslint-enable camelcase */
  };

  getRowData = (headerOrder, rowData) => {
    const { columnWidth } = this.state;
    const tableKeys = headerOrder.map((r) => r.key);

    return tableKeys.map((c) => ({
      id: uuidv4(),
      content: this.getTdData(rowData, c),
      styles: { width: columnWidth },
    }));
  };

  getTableToRender = (cancerHotspotsData) => {
    return flatten(
      cancerHotspotsData.map((c) => {
        const extraHeader = [
          {
            id: uuidv4(),
            content: "Tumor",
            styledAs: "header",
            colSpan: 4,
          },
          {
            id: uuidv4(),
            content: "Normal",
            styledAs: "header",
            colSpan: 4,
          },
          { id: uuidv4(), content: "", styledAs: "header" },
        ];
        const firstRowHeader = firstRowHeaderOrder.map((f) => ({
          id: uuidv4(),
          content: f.label,
          styledAs: "header",
        }));
        const upperTableData = this.getRowData(firstRowHeaderOrder, c);
        const secondRowHeader = secondRowHeaderOrder.map((s) => ({
          id: uuidv4(),
          content: s.label,
          styledAs: "header",
        }));
        const bottomTableData = this.getRowData(secondRowHeaderOrder, c);
        const backgroundColor = approxSnow;
        const borderColor = titaniumWhite;
        const borderStyle = `1px solid ${borderColor}`;

        return [
          {
            id: uuidv4(),
            columns: extraHeader,
            rowStyles: {
              textAlign: "center",
              borderRight: borderStyle,
              borderLeft: borderStyle,
              backgroundColor,
            },
          },
          {
            id: uuidv4(),
            columns: firstRowHeader,
            rowStyles: {
              borderRight: borderStyle,
              borderLeft: borderStyle,
              backgroundColor,
            },
          },
          {
            id: uuidv4(),
            columns: upperTableData,
            rowStyles: {
              borderRight: borderStyle,
              borderLeft: borderStyle,
            },
          },
          {
            id: uuidv4(),
            columns: secondRowHeader,
            rowStyles: {
              borderTop: borderStyle,
              borderRight: borderStyle,
              borderLeft: borderStyle,
              backgroundColor,
            },
          },
          {
            id: uuidv4(),
            columns: bottomTableData,
            rowStyles: {
              borderRight: borderStyle,
              borderLeft: borderStyle,
              borderBottom: borderStyle,
            },
          },
        ];
      })
    );
  };

  render() {
    const { cancerHotspotsData } = this.props;

    if (cancerHotspotsData) {
      const { oncotree_code, cancer_type, cancer_name, tissue_type } =
        cancerHotspotsData;

      const table = this.getTableToRender([cancerHotspotsData]);
      const cancerHotspotsUrl = getVarsoUrl(
        "https://www.cancerhotspots.org/#/home"
      );

      return (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              version={cancerHotspotsData.version}
              url={cancerHotspotsUrl}
              content={
                <div className="varsome-inner-container">
                  <h5> Summary of results </h5>
                  <h6>
                    <b>
                      {"Total samples: "}
                      {cancerHotspotsData.total_samples}
                    </b>
                  </h6>
                  <StyledTableWrapper>
                    <Table className="tw-mt-8" rows={table} />
                  </StyledTableWrapper>
                  <StyledChartsGroup>
                    <Chart
                      showEmptyState={false}
                      // eslint-disable-next-line camelcase
                      data={oncotree_code}
                      title="Oncotree code"
                    />
                    <Chart
                      showEmptyState={false}
                      // eslint-disable-next-line camelcase
                      data={cancer_type}
                      title="Cancer type"
                    />
                    <Chart
                      showEmptyState={false}
                      // eslint-disable-next-line camelcase
                      data={cancer_name}
                      title="Cancer name"
                    />
                    <Chart
                      showEmptyState={false}
                      // eslint-disable-next-line camelcase
                      data={tissue_type}
                      title="Tissue type"
                    />
                  </StyledChartsGroup>
                </div>
              }
              title="Cancer Hotspots"
            />
          </div>
        </div>
      );
    }

    return null;
  }
}

CancerHotspots.propTypes = {
  cancerHotspotsData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    version: PropTypes.string,
    total_samples: PropTypes.number,
    oncotree_code: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    cancer_type: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    cancer_name: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    tissue_type: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.number,
      })
    ),
  }),
};

CancerHotspots.defaultProps = {
  cancerHotspotsData: null,
};

const mapStateToProps = (state) => {
  return {
    cancerHotspotsData: getCancerHotspotsData(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(CancerHotspots, "CancerHotspotsComponent", "Cancer Hotspots"));
